import { all, call, put, takeLatest, delay } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { isAuthenticated } from '../../services/AccessToken';
import { getLocalStorageItem, setGTMData } from '../../services/Helper';
import { historyReplace } from '../../services/History';
import { shopActionTypes, getNumberFromPrice } from './Constants';
import * as sidebarActions from '../../components/sideBar/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as shopActions from './Actions';
import { handlePublicExperiment } from '../publicShop/Sagas';

export default function* () {
	yield all([
		yield takeLatest(shopActionTypes.GET_SHOP_DATA, handleShopRequest),
		yield takeLatest(
			shopActionTypes.GET_PAYMENT_REQUESTS_SHOP_DATA,
			handlePaymentRequestsShopRequest,
		),
		yield takeLatest(shopActionTypes.GET_PAYMENT_METHODS_REQUEST, handlePaymentMethodsRequest),
		yield takeLatest(shopActionTypes.HANDLE_SUBSCRIPTION_REQUEST, handleSubscriptionRequest),
		yield takeLatest(shopActionTypes.GET_FAILED_PAYMENTS_REQUEST, handleFailedPaymentsRequest),
	]);
}

export function* handleShopRequest({ payload }) {
	try {
		const { shopId, paymentType, isPublicShop } = payload || {};
		const discountCode = getLocalStorageItem('discountCode');
		const requestPayload = {};

		if (isPublicShop) yield call(handlePublicExperiment, 'shop-details');

		if (discountCode) {
			requestPayload.discount_code = discountCode;
		}
		if (paymentType) {
			requestPayload.payment_method_type = paymentType;
		}
		const { data } = yield call(
			processRequest,
			`public/packages/${shopId}`,
			'GET',
			requestPayload,
		);

		yield put(shopActions.getShopSuccess(data.package, data.discount_code));
	} catch (e) {
		const {
			response: { status },
		} = e;
		if (status === 400 || status === 404) {
			return yield call(historyReplace, '/my-packages');
		}
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handlePaymentRequestsShopRequest({ payload }) {
	try {
		const { shopId, packageId, paymentType } = payload || {};
		const discountCode = getLocalStorageItem('discountCode');
		let requestPayload = {};

		if (packageId) {
			requestPayload = { package_id: packageId };
		}
		if (paymentType) {
			requestPayload.payment_method_type = paymentType;
		}

		if (discountCode) {
			requestPayload.discount_code = discountCode;
		}

		const { data } = yield call(
			processRequest,
			`payment_requests/${shopId}`,
			'GET',
			requestPayload,
		);

		yield put(
			shopActions.getPaymentRequestsShopSuccess(data.payment_request, data.discount_code),
		);
	} catch (e) {
		const { status, data } = e.response || {};
		if (status === 400 && data && data.hasOwnProperty('email')) {
			yield call(historyReplace, '/already-paid', data);
		}
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handlePaymentMethodsRequest() {
	try {
		const { data } = yield call(processRequest, 'adyen_services/payment_methods');

		yield put(shopActions.getPaymentMethodsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleSubscriptionRequest(action) {
	const { paymentIntentId, pageType, shopData } = action.payload;
	const { chosen_plan_id, preferred_plan_id } = shopData || {};
	const { name, plans } = shopData && shopData.package ? shopData.package : shopData;

	const chosenPlan = plans.filter((item) => item.id.toString() === chosen_plan_id.toString())[0];
	const preferredPlan =
		preferred_plan_id &&
		plans.filter((item) => item.id.toString() === preferred_plan_id.toString())[0];

	const { total_price, identifier } =
		pageType.type === 'isFailedPayments' ? {} : chosenPlan || preferredPlan;

	function* handleStepsAfterSubscriptionCreation() {
		yield put(shopActions.handleSubscriptionSuccess());

		if (pageType.type !== 'isFailedPayments') {
			setGTMData({
				event: 'order-completed',
				transactionId: paymentIntentId,
				transactionAffiliation: name,
				transactionTotal: getNumberFromPrice(total_price),
				transactionProducts: [
					{
						sku: chosen_plan_id.toString(),
						name: identifier,
						category: 'Pakketten',
						price: getNumberFromPrice(total_price),
						quantity: 1,
					},
				],
			});
		}

		if (pageType.type === 'parentPayShop') {
			yield call(historyReplace, `/subscription-payment-success-parent/${paymentIntentId}`);
		} else if (pageType.type === 'publicShop') {
			yield call(
				historyReplace,
				`/subscription-payment-success-public-shop/${paymentIntentId}`,
			);
		} else if (pageType.type === 'isFailedPayments') {
			yield call(historyReplace, '/successfully-restored-payment');
			if (isAuthenticated()) yield put(sidebarActions.getUseDataRequest());
		} else {
			yield call(historyReplace, `/subscription-payment-success/${paymentIntentId}`);
			yield put(sidebarActions.getUseDataRequest());
		}
	}

	try {
		let isFulfilled = false;

		while (!isFulfilled) {
			yield delay(1000);
			const { data } = yield call(processRequest, `payment_intents/${paymentIntentId}`);
			isFulfilled = data.payment_intent.is_fulfilled;
		}

		yield call(handleStepsAfterSubscriptionCreation);
	} catch (e) {
		if (e.response.status === 422) {
			yield call(handleStepsAfterSubscriptionCreation);
		} else yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleFailedPaymentsRequest({ payload }) {
	try {
		const { invoiceId, secret, paymentType } = payload;
		const requestPayload = { secret };

		if (paymentType) {
			requestPayload.payment_method_type = paymentType;
		}
		const { data } = yield call(
			processRequest,
			`public/invoices/${invoiceId}`,
			'GET',
			requestPayload,
		);

		yield put(shopActions.getFailedPaymentsSuccess(data.invoice));
	} catch (e) {
		if (e.response.status === 400) {
			yield call(historyReplace, '/already-fulfilled');
		} else yield put(errorHandlerActions.handleError(e));
	}
}
